/**
 * Minimal header (sections/header.liquid, Header style: Minimal)
 *
 * <header-collections-panel data-trigger-url="/collections">
 *   Opens the collections image panel while the shopper hovers or focuses the header menu link that
 *   points to data-trigger-url, and keeps it open while the pointer is on the panel. Closes on
 *   pointer leave (short delay), Escape, or when focus leaves both. Desktop only (990px and up).
 *
 * Stories button ([data-header-stories]): hidden when the page has no Story viewer with stories,
 * like the mobile bottom bar (the Story viewer opens it through [data-story-open]).
 */
(() => {
  const DESKTOP = '(min-width: 990px)';
  const CLOSE_DELAY = 150;

  const pathOf = (url) => {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '') || '/';
    } catch {
      return '';
    }
  };

  if (!customElements.get('header-collections-panel')) {
    class HeaderCollectionsPanel extends HTMLElement {
      #controller = new AbortController();
      #timer = 0;
      /** @type {HTMLAnchorElement | null} */
      #trigger = null;

      connectedCallback() {
        const header = this.closest('.header-wrapper') || document;
        const target = pathOf(this.dataset.triggerUrl || '/collections');
        // Match "/collections" and its localized form ("/de/collections").
        this.#trigger =
          [...header.querySelectorAll('.header__inline-menu a[href]')].find((link) => {
            const path = pathOf(link.getAttribute('href'));
            return path === target || path.endsWith(target);
          }) || null;
        if (!this.#trigger) return;

        const { signal } = this.#controller;
        const item = this.#trigger.closest('li') || this.#trigger;
        this.#trigger.setAttribute('aria-expanded', 'false');
        this.#trigger.setAttribute('aria-haspopup', 'true');
        if (!this.id) this.id = `HeaderCollectionsPanel-${Math.random().toString(36).slice(2, 8)}`;
        this.#trigger.setAttribute('aria-controls', this.id);

        for (const element of [item, this]) {
          element.addEventListener('pointerenter', (event) => {
            if (event.pointerType !== 'touch') this.#open();
          }, { signal });
          element.addEventListener('pointerleave', () => this.#scheduleClose(), { signal });
          element.addEventListener('focusin', () => this.#open(), { signal });
          element.addEventListener('focusout', (event) => {
            if (!item.contains(event.relatedTarget) && !this.contains(event.relatedTarget)) this.#close();
          }, { signal });
        }
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !this.hidden) {
            this.#close();
            this.#trigger?.focus();
          }
        }, { signal });
        window.matchMedia(DESKTOP).addEventListener('change', () => this.#close(), { signal });
      }

      disconnectedCallback() {
        this.#controller.abort();
        this.#controller = new AbortController();
        clearTimeout(this.#timer);
      }

      #open() {
        if (!window.matchMedia(DESKTOP).matches) return;
        clearTimeout(this.#timer);
        this.hidden = false;
        this.#trigger?.setAttribute('aria-expanded', 'true');
        this.closest('.header-wrapper')?.classList.add('header-wrapper--panel-open');
      }

      #scheduleClose() {
        clearTimeout(this.#timer);
        this.#timer = setTimeout(() => this.#close(), CLOSE_DELAY);
      }

      #close() {
        clearTimeout(this.#timer);
        this.hidden = true;
        this.#trigger?.setAttribute('aria-expanded', 'false');
        this.closest('.header-wrapper')?.classList.remove('header-wrapper--panel-open');
      }
    }

    customElements.define('header-collections-panel', HeaderCollectionsPanel);
  }

  /* Stories button: only when a Story viewer with stories is on the page (always in the theme editor). */
  const toggleStories = () => {
    const viewer = document.querySelector('story-viewer');
    const hasStories = Boolean(viewer) && viewer.dataset.count !== '0';
    for (const button of document.querySelectorAll('[data-header-stories]')) {
      button.hidden = !hasStories && !window.Shopify?.designMode;
    }
  };
  toggleStories();
  document.addEventListener('story-viewer:ready', toggleStories);
  document.addEventListener('shopify:section:load', () => setTimeout(toggleStories, 0));
  document.addEventListener('shopify:section:unload', () => setTimeout(toggleStories, 0));
})();
